import React, { createContext, useContext, useEffect, useState } from 'react';
import { webSocketManager } from '../../services/websocket';
import { WebSocketConnection, RealTimeEvent } from '../../types';
import { useNotifications } from './NotificationProvider';

interface WebSocketContextType {
  isConnected: boolean;
  connectionCount: number;
  events: RealTimeEvent[];
  connect: () => Promise<void>;
  disconnect: () => void;
  clearEvents: () => void;
  getStatus: () => {
    connected: number;
    total: number;
    services: Record<string, WebSocketConnection>;
  };
}

const WebSocketContext = createContext<WebSocketContextType | null>(null);

export function useWebSocketContext() {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within a WebSocketProvider');
  }
  return context;
}

interface WebSocketProviderProps {
  children: React.ReactNode;
  autoConnect?: boolean;
}

export function WebSocketProvider({ children, autoConnect = true }: WebSocketProviderProps) {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionCount, setConnectionCount] = useState(0);
  const [events, setEvents] = useState<RealTimeEvent[]>([]);
  const { addNotification } = useNotifications();

  const updateStatus = () => {
    const status = webSocketManager.getOverallStatus();
    setConnectionCount(status.connected);
    setIsConnected(status.connected > 0);
  };

  const connect = async () => {
    try {
      await webSocketManager.connectAll();
      updateStatus();
      
      addNotification({
        type: 'success',
        title: 'WebSocket Connected',
        message: 'Real-time updates are now active',
      });
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Connection Failed',
        message: 'Failed to establish WebSocket connection',
      });
      throw error;
    }
  };

  const disconnect = () => {
    webSocketManager.disconnectAll();
    updateStatus();
    
    addNotification({
      type: 'info',
      title: 'WebSocket Disconnected',
      message: 'Real-time updates are now inactive',
    });
  };

  const clearEvents = () => {
    setEvents([]);
  };

  const getStatus = () => {
    return webSocketManager.getOverallStatus();
  };

  const addEvent = (event: RealTimeEvent) => {
    setEvents(prev => [event, ...prev.slice(0, 99)]); // Keep last 100 events
    
    // Show notifications for critical events
    if (event.severity === 'critical' || event.severity === 'error') {
      addNotification({
        type: event.severity === 'critical' ? 'error' : 'warning',
        title: `Real-time Alert`,
        message: event.message,
      });
    }
  };

  useEffect(() => {
    // Set up global event listeners for all WebSocket services
    const setupGlobalListeners = () => {
      // This is a simplified approach - in a real implementation,
      // you might want to set up listeners dynamically as services are created
      
      // Analytics events
      const analyticsService = webSocketManager.getService('analytics');
      analyticsService.subscribe('message', (message) => {
        if (message.type === 'alert' || message.type === 'error') {
          const event: RealTimeEvent = {
            id: message.id || `event_${Date.now()}`,
            type: message.type === 'alert' ? 'alert_triggered' : 'system_event',
            severity: message.type === 'error' ? 'error' : 'warning',
            message: message.data?.message || 'System event occurred',
            data: message.data,
            timestamp: message.timestamp,
            source: 'analytics',
          };
          addEvent(event);
        }
      });

      // Health monitoring events
      const healthService = webSocketManager.getService('health');
      healthService.subscribe('message', (message) => {
        if (message.type === 'health' && message.data?.status !== 'healthy') {
          const event: RealTimeEvent = {
            id: message.id || `health_${Date.now()}`,
            type: 'system_event',
            severity: message.data.status === 'down' ? 'critical' : 'warning',
            message: `Health check: ${message.data.service} is ${message.data.status}`,
            data: message.data,
            timestamp: message.timestamp,
            source: 'health',
          };
          addEvent(event);
        }
      });

      // Connection status events
      const connectionHandler = (message: any) => {
        updateStatus();
        
        if (message.data?.status === 'connected') {
          const event: RealTimeEvent = {
            id: `connection_${Date.now()}`,
            type: 'connection_status',
            severity: 'info',
            message: 'WebSocket connection established',
            timestamp: new Date().toISOString(),
            source: 'websocket',
          };
          addEvent(event);
        } else if (message.data?.status === 'disconnected') {
          const event: RealTimeEvent = {
            id: `disconnection_${Date.now()}`,
            type: 'connection_status',
            severity: 'warning',
            message: 'WebSocket connection lost',
            timestamp: new Date().toISOString(),
            source: 'websocket',
          };
          addEvent(event);
        }
      };

      analyticsService.subscribe('connection', connectionHandler);
      healthService.subscribe('connection', connectionHandler);
    };

    setupGlobalListeners();

    // Auto-connect if requested
    if (autoConnect) {
      connect().catch(error => {
        console.error('Auto-connect failed:', error);
      });
    }

    // Periodic status updates
    const statusInterval = setInterval(updateStatus, 5000);

    return () => {
      clearInterval(statusInterval);
    };
  }, [autoConnect]);

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is hidden - could reduce update frequency
      } else {
        // Page is visible - ensure connections are active
        updateStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      webSocketManager.disconnectAll();
    };
  }, []);

  const contextValue: WebSocketContextType = {
    isConnected,
    connectionCount,
    events,
    connect,
    disconnect,
    clearEvents,
    getStatus,
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
}